<template>
  <div class="min-h-screen bg-gray-50 px-4 pt-6 pb-28" dir="rtl">
    <div class="max-w-2xl mx-auto space-y-6">
      
      <!-- Header Card -->
      <div class="bg-gradient-to-r from-orange-500 to-amber-600 rounded-xl p-6 text-white">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 rounded-full bg-white bg-opacity-20 backdrop-blur-sm flex items-center justify-center">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div class="w-full">
            <h3 class="text-xl font-bold">فاکتورهای پرداخت نشده</h3>
            <p class="text-sm text-orange-50">برای انتخاب، روی فاکتور یا چک‌باکس کلیک کنید</p>
          </div>
        </div>
      </div>

      <!-- Skeleton -->
      <div v-if="pageLoading" class="pay-report-skeleton-group space-y-4" aria-hidden="true">
        <div class="skeleton-block skeleton-stats-card"></div>
        <div
          v-for="n in skeletonCardCount"
          :key="'unpaid-sk-' + n"
          class="skeleton-block skeleton-invoice-card"
        ></div>
      </div>

      <template v-else-if="list.length > 0">
      <!-- Stats Summary -->
      <div class="bg-white rounded-xl border border-gray-200 p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <svg class="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div class="w-full">
              <p class="text-xs text-gray-500">تعداد فاکتورها</p>
              <p class="text-lg font-bold text-gray-900">{{ list.length }} فاکتور</p>
            </div>
          </div>
          <div class="text-left">
            <p class="text-xs text-gray-500">انتخاب شده</p>
            <p class="text-lg font-bold text-orange-600">{{ selectedCount }} مورد</p>
          </div>
        </div>
      </div>

      <!-- Invoice List -->
      <div class="space-y-4">
        <div class="space-y-3">
          <div 
            v-for="(item, index) in list" 
            :key="'unpaid-' + (item.MasterFactorId || item.FactorCode || 'x') + '-' + index"
            :class="item.selected ? 'bg-orange-50 border-orange-300' : 'bg-white border-gray-200'"
            class="p-4 border rounded-lg transition-all duration-200 hover:shadow-md cursor-pointer"
            @click="toggleSelect(item)"
          >
            <!-- Invoice Header -->
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-2">
                <label
                  class="pay-checkbox"
                  :class="{ 'is-checked': item.selected }"
                  @click.stop.prevent="toggleSelect(item)"
                >
                  <input
                    type="checkbox"
                    class="pay-checkbox-input"
                    :checked="!!item.selected"
                    tabindex="-1"
                    @click.prevent
                  >
                  <span class="pay-checkbox-box" aria-hidden="true">
                    <svg v-if="item.selected" class="pay-checkbox-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                </label>
                <span class="text-sm text-gray-900">{{ item.RequestName }}</span>
              </div>
              <span class="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full whitespace-nowrap">
                پرداخت نشده
              </span>
            </div>

            <!-- Invoice Details -->
            <div class="flex justify-between gap-3 text-sm">
              <div class="flex  gap-1">
                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
                <span class="text-gray-500">کد درخواست:</span>
                <span class="font-medium text-gray-900">{{ item.RequestCode }}</span>
              </div>
              <div class="flex  gap-1">
                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span class="text-gray-500">کد فاکتور:</span>
                <span class="font-medium text-gray-900">{{ item.FactorCode }}</span>
              </div>
            </div>

            <div class="flex gap-1 mt-2 text-sm">
              <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span class="text-gray-500">تاریخ سند:</span>
              <span class="font-medium text-gray-900">{{ item.DocDate }}</span>
            </div>

            <!-- Amount -->
            <div class="mt-3 pt-3 border-t border-dashed border-gray-200 flex items-center justify-between">
              <span class="text-sm text-gray-500">مبلغ فاکتور:</span>
              <span class="text-lg font-bold text-orange-600">
                {{ formatNumber(item.Amount) }}
                <span class="text-xs font-normal text-gray-500">تومان</span>
              </span>
            </div>
          </div>
        </div>
      </div>
      </template>

      <!-- Empty State -->
      <div v-else-if="!pageLoading" class="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
        <div class="max-w-2xl mx-auto">
          <div class="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <svg class="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p class="text-gray-700 font-medium mb-2">فاکتور پرداخت نشده‌ای وجود ندارد</p>
          <p class="text-gray-400 text-sm">تمام فاکتورهای شما پرداخت شده است</p>
        </div>
      </div>

    </div>

    <!-- Pay Button -->
    <div v-if="!pageLoading && list.length > 0" class="fixed bottom-24 left-0 right-0 px-4 flex justify-center z-10">
      <button 
        @click="openPayModal()"
        :disabled="selectedCount === 0"
        :class="selectedCount > 0 
          ? 'bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-lg shadow-orange-200' 
          : 'bg-gray-300 text-gray-500 cursor-not-allowed'"
        class="w-full max-w-2xl py-4 px-6 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
        <span v-if="selectedCount > 0">پرداخت {{ selectedCount }} فاکتور</span>
        <span v-else>فاکتوری انتخاب نشده</span>
      </button>
    </div>

    <!-- Payment Modal -->
    <div
      v-if="showPayModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 pt-20"
      style="z-index: 999;"
      @click.self="closePayModal()"
    >
      <div class="bg-white rounded-2xl sm:rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
        <div class="bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-4 text-white rounded-t-2xl">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-bold w-full">انتخاب روش پرداخت</h2>
            <button
              type="button"
              class="w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0"
              @click="closePayModal()"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div class="px-6 py-5 space-y-4">
          <div class="bg-gray-50 rounded-xl p-4 space-y-3">
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-500">تعداد فاکتور انتخاب‌شده</span>
              <span class="font-bold text-gray-900">{{ selectedCount }} مورد</span>
            </div>
          
            <div v-if="useWallet && walletDeductAmount > 0" class="flex items-center justify-between text-sm">
              <span class="text-green-600">کسر از کیف پول</span>
              <span class="font-bold text-green-600">{{ formatNumber(walletDeductAmount) }} - تومان</span>
            </div>
            <div class="pt-3 border-t border-dashed border-gray-200 flex items-center justify-between">
              <span class="font-medium text-gray-700">مبلغ قابل پرداخت</span>
              <span class="text-xl font-bold text-orange-600">
                {{ formatNumber(payableAmount) }}
                <span class="text-sm font-normal text-gray-500">تومان</span>
              </span>
            </div>
          </div>

          <button
            type="button"
            :disabled="walletBalance <= 0 || walletLoading"
            :class="useWallet
              ? 'border-orange-400 bg-orange-50'
              : 'border-gray-200 bg-white hover:border-orange-300'"
            class="w-full p-4 border rounded-xl transition-all duration-200 text-right disabled:opacity-50 disabled:cursor-not-allowed"
            @click="toggleUseWallet()"
          >
            <div class="flex items-start gap-3">
              <div
                :class="useWallet ? 'bg-orange-600 border-orange-600' : 'bg-white border-gray-300'"
                class="relative w-6 h-6 flex-shrink-0 border rounded-md inline-flex items-center justify-center mt-0.5"
              >
                <svg
                  v-if="useWallet"
                  class="absolute top-1/2 left-1/2 w-3.5 h-3.5 -translate-x-1/2 -translate-y-1/2 text-white block pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div class="flex-1">
                <p class="font-bold text-gray-900">کسر از کیف پول</p>
                <p v-if="walletLoading" class="text-sm text-orange-600 font-bold mt-1">در حال دریافت...</p>
                <p v-else class="text-sm text-orange-600 font-bold mt-1">
                  {{ formatNumber(walletBalance) }} تومان
                </p>
              </div>
            </div>
          </button>

          
        </div>

        <div class="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button
            type="button"
            class="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
            :disabled="paying"
            @click="closePayModal()"
          >
            انصراف
          </button>
          <button
            type="button"
            class="flex-1 px-6 py-3 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="paying || walletLoading"
            @click="confirmPay()"
          >
            {{ paying ? 'در حال پردازش...' : 'تایید و پرداخت' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Wallet Payment Success -->
    <div
      v-if="showWalletSuccess"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 px-4"
      style="z-index: 999;"
    >
      <div class="bg-white rounded-2xl shadow-xl px-8 py-10 text-center w-full max-w-xs">
        <div class="mx-auto mb-4 flex items-center justify-center" style="width: 100px; height: 100px">
          <lottie
            v-if="checkedAnimationOptions.animationData"
            :options="checkedAnimationOptions"
            :height="100"
            :width="100"
          />
        </div>
        <p class="text-lg font-bold text-gray-900">با موفقیت پرداخت شد</p>
      </div>
    </div>
  </div>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading'
import Vue from 'vue'
import Treeselect, {ASYNC_SEARCH} from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import {bus} from '../../main'
import Lottie from '../../Lottie.vue'
import { skeletonSilentRequest } from '../../utils/skeletonPages'

export default {
  props: ['passing_data'],

  data() {
    return {
      list: [],
      pageLoading: true,
      skeletonCardCount: 4,
      showPayModal: false,
      useWallet: false,
      walletBalance: 0,
      walletId: 21,
      walletLoading: false,
      paying: false,
      showWalletSuccess: false,
      walletSuccessTimer: null,
      checkedAnimationOptions: { animationData: null, loop: false },
    }
  },
  beforeDestroy() {
    if (this.walletSuccessTimer) {
      clearTimeout(this.walletSuccessTimer)
    }
  },
  components: {
    InfiniteLoading,
    Treeselect,
    lottie: Lottie,
  },
  computed: {
    selectedCount() {
      return this.selectedItems.length
    },
    selectedItems() {
      return this.list.filter(x => x.selected === true)
    },
    totalSelectedAmount() {
      return this.selectedItems
        .reduce((sum, item) => sum + parseInt(item.Amount || 0, 10), 0)
    },
    walletDeductAmount() {
      if (!this.useWallet) return 0;
      return Math.min(this.walletBalance, this.totalSelectedAmount);
    },
    payableAmount() {
      return Math.max(this.totalSelectedAmount - this.walletDeductAmount, 0);
    },
  },
  mounted() {
    this.get_data();
    this.loadCheckedAnimation();
  },

  methods: {
    loadCheckedAnimation() {
      fetch('/assets/checked.json')
        .then(res => res.json())
        .then(data => {
          this.checkedAnimationOptions = { animationData: data, loop: false, autoplay: true }
        })
        .catch(() => {})
    },
    formatNumber(num) {
      if (!num) return '0';
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    toggleSelect(item) {
      if (!item) return
      Vue.set(item, 'selected', !item.selected)
    },
    parseWalletDetails(data) {
      if (data == null) return null
      if (typeof data === 'string') {
        const trimmed = data.trim()
        if (!trimmed) return null
        try {
          return this.parseWalletDetails(JSON.parse(trimmed))
        } catch (e) {
          return null
        }
      }
      if (Array.isArray(data)) {
        return data.find(x => x && (x.AccBalance != null || x.AccValue != null || x.WalletId != null || x.RecId != null)) || data[0] || null
      }
      if (data.items && Array.isArray(data.items)) {
        return this.parseWalletDetails(data.items)
      }
      if (data.Data != null) {
        return this.parseWalletDetails(data.Data)
      }
      if (data.data != null) {
        return this.parseWalletDetails(data.data)
      }
      return typeof data === 'object' ? data : null
    },
    applyWalletDetails(data) {
      const details = this.parseWalletDetails(data)
      const rawBalance = details && (details.AccBalance ?? details.accBalance)
      this.walletBalance = Math.max(parseInt(rawBalance, 10) || 0, 0)
      this.walletId = 21
    },
    openPayModal() {
      if (this.selectedCount === 0) return;
      this.useWallet = false;
      this.showPayModal = true;
      this.fetchWalletBalance();
    },
    closePayModal() {
      if (this.paying) return;
      this.showPayModal = false;
      this.useWallet = false;
    },
    toggleUseWallet() {
      if (this.walletBalance <= 0 || this.walletLoading) return;
      this.useWallet = !this.useWallet;
    },
    fetchWalletBalance() {
      const profile = JSON.parse(localStorage.getItem('profile') || '{}')
      if (!profile.UserId) return Promise.resolve()
      this.walletLoading = true
      return this.axios.post('/GetDashAccBalanceDetailsForApp', { UserId: profile.UserId }, { show_pros: false })
        .then(response => {
          this.applyWalletDetails(response.data)
        })
        .catch(() => {
          this.walletBalance = 0
        })
        .finally(() => {
          this.walletLoading = false
        })
    },
    get_data() {
      var self = this
      var user_id = JSON.parse(localStorage.getItem('profile')).UserId
      const send_data = {
        UserId: user_id,
        txtCode: "",
        fdate: "",
        sdate: "",
      }

      this.axios.post('/GetFactorUnPaid', send_data, skeletonSilentRequest)
        .then(response => {
          self.list = (response.data.items || []).map(item => ({
            ...item,
            selected: false,
          }))
        }).catch(e => {
        this.errors.push(e)
      }).finally(() => {
        self.pageLoading = false
      })
    },
    normalizeMobile(phone) {
      if (!phone) return '';
      // حذف فاصله و کاراکترهای اضافی
      phone = phone.toString().trim().replace(/[\s\-]/g, '');
      // اگر با +98 شروع شود
      if (phone.startsWith('+98')) {
        return '0' + phone.substring(3);
      }
      // اگر با 98 شروع شود (بدون +)
      if (phone.startsWith('98') && phone.length > 10) {
        return '0' + phone.substring(2);
      }
      // اگر با 9 شروع شود (بدون 0)
      if (phone.startsWith('9') && phone.length === 10) {
        return '0' + phone;
      }
      // اگر با 09 شروع شود، همان را برگردان
      return phone;
    },
    async confirmPay() {
      if (this.useWallet) {
        if (!this.walletId) {
          await this.fetchWalletBalance()
        }
        await this.payViaWallet()
        return
      }
      this.add_pay_factor()
    },
    isApiSuccess(data) {
      if (!data || typeof data !== 'object') return false
      return data.error == 0
    },
    showWalletPaymentSuccess() {
      this.showPayModal = false
      this.useWallet = false
      this.showWalletSuccess = true
      if (this.walletSuccessTimer) {
        clearTimeout(this.walletSuccessTimer)
      }
      this.walletSuccessTimer = setTimeout(() => {
        this.showWalletSuccess = false
        this.walletSuccessTimer = null
        this.get_data()
      }, 2000)
    },
    buildWalletFactors(selected) {
      return selected.map(item => ({
        FactorMasterId: parseInt(item.MasterFactorId, 10) || 0,
        FactorCode: String(item.FactorCode || ''),
        Amount: parseInt(item.Amount, 10) || 0,
        WalletId: this.walletId,
      }))
    },
    async payViaWallet() {
      const selected = this.selectedItems
      if (!selected.length) return false

      const profile = JSON.parse(localStorage.getItem('profile') || '{}')
      if (!profile.UserId) {
        bus.$emit('show_toast', [2000, 'اطلاعات کاربر در دسترس نیست'])
        return false
      }

      const factors = this.buildWalletFactors(selected)
      const payload = {
        UserId: String(profile.UserId),
        Factors: JSON.stringify(factors),
      }

      this.paying = true
      try {
        const response = await this.axios.post('/AddCashFromWallet', payload, {})
        if (!this.isApiSuccess(response.data)) {
          bus.$emit('show_toast', [2000, (response.data && response.data.msg) || 'خطا در پرداخت از کیف پول'])
          return false
        }
        this.showWalletPaymentSuccess()
        return true
      } catch (e) {
        bus.$emit('show_toast', [2000, 'خطا در پرداخت از کیف پول'])
        return false
      } finally {
        this.paying = false
      }
    },
    add_pay_factor() {
      var self = this
      let pros = []
      this.selectedItems.forEach(function (val) {
        pros.push({
          "name": val.RequestCode,
          "price": val.Amount,
          "summary": val.MasterFactorId,
        })
      })

      var formData = new FormData();
      formData.append("products", JSON.stringify(pros))
      formData.append("customer_mobile", this.normalizeMobile(localStorage.getItem("phone")))
      formData.append("customer_name", JSON.parse(localStorage.getItem('profile')).UserName)
      formData.append("economic_code", "")
      formData.append("document_code", "سهم فروش")
      formData.append("summary", "سند پرداخت فروش های انجام شده")
      formData.append("use_wallet", "0")
      formData.append("wallet_amount", "0")
      formData.append("payable_amount", String(this.payableAmount))
      formData.append("total_amount", String(this.totalSelectedAmount))
      formData.append("user_id", String(JSON.parse(localStorage.getItem('profile')).UserId))

      this.paying = true
      this.axios.post('https://pay.zoodkomak.com/api/add_factor_from_zoodkomak', formData, {})
        .then(response => {
          if (response.data.error == 0) {
            self.showPayModal = false;
            if (self.payableAmount > 0 && response.data.unique_code) {
              var url = "https://pay.zoodkomak.com/i/" + response.data.unique_code;
              window.open(url, "_self")
            } else {
              bus.$emit('show_toast', [2000, response.data.msg || 'پرداخت با موفقیت انجام شد'])
              self.get_data()
            }
          } else {
            bus.$emit('show_toast', [2000, response.data.msg])
          }
        }).catch(e => {
        this.errors.push(e)
      }).finally(() => {
        self.paying = false
      })
    },
  }
}
</script>
<style scoped>
.pay-checkbox {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
  cursor: pointer;
}

.pay-checkbox-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
}

.pay-checkbox-box {
  width: 1.5rem;
  height: 1.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.pay-checkbox.is-checked .pay-checkbox-box {
  background: #ea580c;
  border-color: #ea580c;
}

.pay-checkbox-icon {
  width: 0.9rem;
  height: 0.9rem;
  color: #fff;
  display: block;
  pointer-events: none;
}

.pay-report-skeleton-group {
  position: relative;
  overflow: hidden;
  pointer-events: none;
  user-select: none;
}

.pay-report-skeleton-group::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.55) 45%,
    rgba(255, 255, 255, 0.65) 55%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: pay-report-skeleton-shimmer 1.4s ease-in-out infinite;
}

.pay-report-skeleton-group .skeleton-block {
  background-color: #e5e7eb;
}

.skeleton-stats-card {
  height: 4.5rem;
  border-radius: 0.75rem;
}

.skeleton-invoice-card {
  min-height: 9.5rem;
  border-radius: 0.5rem;
}

@keyframes pay-report-skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}
</style>
